import type { GameManifest } from '@triveelo/ads-core';

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 16;

/** Client-side username rules (3 to 16 letters, numbers or underscores). The API also filters profanity and checks it is free. */
export function usernameProblem(value: string): string | null {
  const u = value.trim();
  if (u.length === 0) return 'Choose a username.';
  if (u.length < USERNAME_MIN) return `At least ${USERNAME_MIN} characters.`;
  if (u.length > USERNAME_MAX) return `No more than ${USERNAME_MAX} characters.`;
  if (!/^[A-Za-z0-9_]+$/.test(u)) return 'Letters, numbers and underscores only.';
  return null;
}

/** Up to two initials for an avatar placeholder. */
export function initials(username: string): string {
  const parts = username.replace(/[^A-Za-z0-9_]/g, '').split('_').filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : (parts[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

const AVATAR_TONES = ['bg-primary/25 text-primary', 'bg-warning/20 text-warning', 'bg-error/20 text-error', 'bg-white/15 text-white', 'bg-success/20 text-success'];

/** Stable tone per username so a player's placeholder never changes colour. */
export function avatarTone(username: string): string {
  let hash = 0;
  for (const ch of username.toLowerCase()) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length]!;
}

export function formatXp(xp: number): string {
  return `${Math.max(0, Math.round(Number.isFinite(xp) ? xp : 0)).toLocaleString('en-GB')} XP`;
}

/** Cities a player can pick for a country, from the zones of live games. */
export function citiesFor(games: Pick<GameManifest, 'country' | 'zones'>[], country: string): string[] {
  const set = new Set<string>();
  for (const game of games) {
    if ((game.country ?? 'NG') !== country) continue;
    for (const zone of game.zones) if (zone.city) set.add(zone.city);
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'en-GB'));
}

/** True only when the user typed the exact confirmation phrase (case-insensitive, trimmed). */
export function confirmsDeletion(typed: string, username: string): boolean {
  const expected = username.trim().toLowerCase();
  return expected.length > 0 && typed.trim().toLowerCase() === expected;
}

const BADGE_LABELS: Record<string, string> = {
  early_rider: 'Early rider',
  first_run: 'First run',
  level_5: 'Level 5',
  level_10: 'Level 10',
  claimed: 'Guest progress claimed',
  fantasy_champion: 'Fantasy champion',
  fantasy_podium: 'Fantasy podium',
  fantasy_season: 'Fantasy season played',
};

/** Badge ids from the API as readable labels; unknown ids are humanised. */
export function badgeLabel(id: string): string {
  if (BADGE_LABELS[id]) return BADGE_LABELS[id]!;
  const words = id.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}
